import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Typography,
  IconButton,
  Paper,
  Link
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_ENDPOINTS } from '../config';

interface HomeAnalysisProps {
  propertyId: string | null;
  userId: string;
  propertyDetails: any | null;
}

interface AnalysisResult {
  imageUrl: string;
  analysis: string;
  created_at: string;
}

const markdownStyles = {
  section: {
    marginBottom: 3,
  },
  heading: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
    marginBottom: 1,
    marginTop: 2,
  },
  subheading: {
    fontWeight: 'bold',
    color: '#555',
    marginBottom: 1,
  },
  recommendations: {
    backgroundColor: '#f5f5f5',
    padding: 2,
    borderRadius: 1,
    marginTop: 1,
  },
};

// Extend the styled markdown container
const MarkdownContainer = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{
    '& p': { 
      margin: '0.5em 0',
      lineHeight: 1.6 
    },
    '& ul, & ol': { 
      margin: '0.5em 0',
      paddingLeft: '1.5em'
    },
    '& li': { 
      margin: '0.25em 0',
      lineHeight: 1.6
    },
    '& strong': { 
      color: '#2c3e50',
      fontWeight: 600
    },
    '& a': {
      color: '#2196f3',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '1em 0 0.5em',
      fontWeight: 600,
      lineHeight: 1.3
    },
    '& hr': {
      margin: '1em 0',
      borderColor: 'divider'
    }
  }}>
    {children}
  </Box>
);

export const HomeAnalysis: React.FC<HomeAnalysisProps> = ({ propertyId, userId, propertyDetails }) => {
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyses, setAnalyses] = useState<AnalysisResult[]>([]);

  // Load existing analyses when component mounts
  useEffect(() => {
    if (propertyId) {
      loadExistingAnalyses();
    }
  }, [propertyId]);

  const loadExistingAnalyses = async () => {
    try {
      if (!propertyId) return;

      const { data, error } = await supabase
        .from('property_images')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const formattedAnalyses = data.map(item => ({
          imageUrl: item.image_url,
          analysis: item.analysis,
          created_at: item.created_at
        }));
        
        setAnalyses(formattedAnalyses);
      }
    } catch (err: any) {
      console.error('Error loading analyses:', err);
    }
  };
  
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    if (!propertyId) {
      setError('Please enter your address first to enable image analysis.');
      return;
    }

    const file = event.target.files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPEG, PNG, or WebP)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // 1. Upload image to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${userId}/${propertyId}/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('home-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // 2. Get public URL for the uploaded image
      const { data: { publicUrl } } = supabase.storage
        .from('home-images')
        .getPublicUrl(filePath);

      // Wait a moment to ensure the file is available
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 3. Get AI analysis
      setAnalyzing(true);
      const response = await fetch(API_ENDPOINTS.analyzeImage, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: publicUrl,
          propertyDetails,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze image');
      }

      const analysisResult = await response.json();
      
      // Check if analysis exists in the expected format
      const analysisContent = analysisResult.analysis; // Changed from analysisResult.response.content
      
      if (!analysisContent) {
        throw new Error('Invalid analysis result format from API');
      }

      // 4. Save analysis to database
      const { error: dbError } = await supabase
        .from('property_images')
        .insert({
          property_id: propertyId,
          user_id: userId,
          image_url: publicUrl,
          analysis: analysisContent,
        });

      if (dbError) throw dbError;

      // 5. Update UI
      setAnalyses([
        {
          imageUrl: publicUrl,
          analysis: analysisContent,
          created_at: new Date().toISOString(),
        },
        ...analyses,
      ]);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload and analyze image');
    } finally {
      setUploading(false);
      setAnalyzing(false);
    }
  };

  const handleDelete = async (imageUrl: string) => {
    try {
      // Extract the file path from the URL
      const urlPath = new URL(imageUrl).pathname;
      const filePath = urlPath.split('/').slice(-3).join('/'); // Gets userId/propertyId/filename
      
      // Delete from Supabase Storage
      const { error: deleteError } = await supabase.storage
        .from('home-images')
        .remove([filePath]);

      if (deleteError) throw deleteError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('property_images')
        .delete()
        .eq('image_url', imageUrl);

      if (dbError) throw dbError;

      // Update UI
      setAnalyses(analyses.filter(a => a.imageUrl !== imageUrl));
    } catch (err: any) {
      setError(err.message || 'Failed to delete image');
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Home Exterior Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload photos of your home's exterior to get AI-powered recommendations for wildfire safety improvements.
        </Typography>
        
        <Button
          component="label"
          variant="contained"
          startIcon={<CloudUploadIcon />}
          disabled={uploading || analyzing}
          sx={{ mt: 2 }}
        >
          Upload Photo
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploading || analyzing}
          />
        </Button>

        {(uploading || analyzing) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {uploading ? 'Uploading...' : 'Analyzing...'}
            </Typography>
          </Box>
        )}

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2}>
        {analyses.map((item, index) => (
          <Grid item xs={12} key={index}>
            <Card sx={{ mb: 3, position: 'relative' }}>
              <CardMedia
                component="img"
                height="300"
                image={item.imageUrl}
                alt="Home exterior"
                sx={{ objectFit: 'cover' }}
              />
              <IconButton
                onClick={() => handleDelete(item.imageUrl)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analysis from {new Date(item.created_at).toLocaleDateString()}
                </Typography>
                
                {/* Replace the custom parsing with ReactMarkdown */}
                <Box sx={{ mt: 2 }}>
                  <MarkdownContainer>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h1: ({node, children, ...props}) => <Typography variant="h4" {...props}>{children}</Typography>,
                        h2: ({node, children, ...props}) => <Typography variant="h5" {...props}>{children}</Typography>,
                        h3: ({node, children, ...props}) => <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }} {...props}>{children}</Typography>,
                        p: ({node, children, ...props}) => <Typography variant="body1" paragraph {...props}>{children}</Typography>,
                        a: ({node, children, ...props}) => <Link target="_blank" rel="noopener noreferrer" {...props}>{children}</Link>,
                        ul: ({node, children, ...props}) => <ul style={{ listStyleType: 'disc' }} {...props}>{children}</ul>,
                        ol: ({node, children, ...props}) => <ol style={{ listStyleType: 'decimal' }} {...props}>{children}</ol>,
                      }}
                    >
                      {item.analysis}
                    </ReactMarkdown>
                  </MarkdownContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};
